import React, { useState, useEffect } from 'react';
import { supabase } from '@/lib/supabase';
import * as LucideIcons from 'lucide-react';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';

interface PageStatus {
  id: string;
  route: string;
  status: 'red' | 'yellow' | 'green';
  label: string | null;
  notes: string | null;
  visible: boolean;
  category_id: string | null;
  order: number;
  icon: string | null;
  updated_at: string;
}

interface MenuCategory {
  id: string;
  name: string;
  emoji: string;
  order: number;
  visible: boolean;
  created_at: string;
  updated_at: string;
}

const PageStatusPage: React.FC = () => {
  const [pageStatuses, setPageStatuses] = useState<PageStatus[]>([]);
  const [categories, setCategories] = useState<MenuCategory[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<'all' | 'red' | 'yellow' | 'green'>('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [editingCategory, setEditingCategory] = useState<MenuCategory | null>(null);
  const [newCategoryName, setNewCategoryName] = useState('');
  const [newCategoryEmoji, setNewCategoryEmoji] = useState('');
  const [showCategoryManager, setShowCategoryManager] = useState(false);
  const [editingPageId, setEditingPageId] = useState<string | null>(null);
  const [showMergeTool, setShowMergeTool] = useState(false);

  // Drag and drop handlers
  const handleDragEnd = async (result: any) => {
    if (!result.destination) return;

    const items = Array.from(filteredPages);
    const [reorderedItem] = items.splice(result.source.index, 1);
    items.splice(result.destination.index, 0, reorderedItem);

    // Update order values in database
    const updates = items.map((item, index) => ({
      id: item.id,
      order: (index + 1) * 10 // Space orders by 10 for easy manual adjustment
    }));

    for (const update of updates) {
      await updatePageOrder(update.id, update.order);
    }
  };

  // Merge duplicate categories
  const mergeDuplicateCategories = async () => {
    try {
      const categoryCounts = new Map<string, number>();
      const categoryIds = new Map<string, string>();

      // Count occurrences and keep first ID for each category name
      categories.forEach(cat => {
        const count = categoryCounts.get(cat.name) || 0;
        categoryCounts.set(cat.name, count + 1);
        if (!categoryIds.has(cat.name)) {
          categoryIds.set(cat.name, cat.id);
        }
      });

      // Find duplicates
      const duplicates = Array.from(categoryCounts.entries())
        .filter(([name, count]) => count > 1);

      if (duplicates.length === 0) {
        alert('✅ No duplicate categories found!');
        return;
      }

      let mergedCount = 0;
      for (const [categoryName, count] of duplicates) {
        const keepId = categoryIds.get(categoryName)!;
        const duplicateCategories = categories.filter(cat => cat.name === categoryName && cat.id !== keepId);

        for (const dupCat of duplicateCategories) {
          // Move all pages from duplicate category to the main one
          await supabase
            .from('page_status')
            .update({ category_id: keepId })
            .eq('category_id', dupCat.id);

          // Delete the duplicate category
          await supabase
            .from('menu_categories')
            .delete()
            .eq('id', dupCat.id);

          mergedCount++;
        }
      }

      alert(`✅ Successfully merged ${mergedCount} duplicate categories!`);
      await loadData();
    } catch (err) {
      console.error('Error merging categories:', err);
      alert('❌ Failed to merge categories. Check console for details.');
    }
  };

  // Common icons for pages
  const commonIcons = [
    'LayoutDashboard', 'Users', 'UserCircle', 'Building2', 'Phone', 'MessageSquare',
    'Mic', 'Shield', 'CreditCard', 'FileText', 'Truck', 'Star', 'MapPin',
    'Calendar', 'Clock', 'Settings', 'Database', 'BarChart', 'PieChart',
    'TrendingUp', 'Package', 'Inbox', 'Send', 'Mail', 'Bell', 'Search',
    'Filter', 'Download', 'Upload', 'Zap', 'Target', 'Award', 'Tag'
  ];

  useEffect(() => {
    loadData();
    
    // Set up real-time subscriptions
    const pageChannel = supabase
      .channel('page_status_changes')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'page_status' }, () => {
        loadData();
      })
      .subscribe();

    const categoryChannel = supabase
      .channel('menu_categories_changes')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'menu_categories' }, () => {
        loadData();
      })
      .subscribe();

    return () => {
      supabase.removeChannel(pageChannel);
      supabase.removeChannel(categoryChannel);
    };
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      
      // Load page statuses
      const { data: pages, error: pagesError } = await supabase
        .from('page_status')
        .select('*')
        .order('route');

      if (pagesError) throw pagesError;
      setPageStatuses(pages || []);

      // Load categories
      const { data: cats, error: catsError } = await supabase
        .from('menu_categories')
        .select('*')
        .order('order');

      if (catsError) throw catsError;
      setCategories(cats || []);
    } catch (err) {
      console.error('Error loading data:', err);
    } finally {
      setLoading(false);
    }
  };

  const updateStatus = async (id: string, newStatus: 'red' | 'yellow' | 'green') => {
    try {
      const { error } = await supabase
        .from('page_status')
        .update({ status: newStatus })
        .eq('id', id);

      if (error) throw error;
      
      setPageStatuses(prev =>
        prev.map(ps => ps.id === id ? { ...ps, status: newStatus } : ps)
      );
    } catch (err) {
      console.error('Error updating status:', err);
      alert('Failed to update status');
    }
  };

  const updatePageVisibility = async (id: string, visible: boolean) => {
    try {
      const { error } = await supabase
        .from('page_status')
        .update({ visible })
        .eq('id', id);

      if (error) throw error;
      
      setPageStatuses(prev =>
        prev.map(ps => ps.id === id ? { ...ps, visible } : ps)
      );
    } catch (err) {
      console.error('Error updating visibility:', err);
      alert('Failed to update visibility');
    }
  };

  const updatePageCategory = async (id: string, categoryId: string | null) => {
    try {
      const { error } = await supabase
        .from('page_status')
        .update({ category_id: categoryId })
        .eq('id', id);

      if (error) throw error;
      
      setPageStatuses(prev =>
        prev.map(ps => ps.id === id ? { ...ps, category_id: categoryId } : ps)
      );
    } catch (err) {
      console.error('Error updating category:', err);
      alert('Failed to update category');
    }
  };

  const updatePageOrder = async (id: string, order: number) => {
    try {
      const { error } = await supabase
        .from('page_status')
        .update({ order })
        .eq('id', id);

      if (error) throw error;
      
      setPageStatuses(prev =>
        prev.map(ps => ps.id === id ? { ...ps, order } : ps)
      );
    } catch (err) {
      console.error('Error updating order:', err);
      alert('Failed to update order');
    }
  };

  const updatePageIcon = async (id: string, icon: string | null) => {
    try {
      const { error } = await supabase
        .from('page_status')
        .update({ icon })
        .eq('id', id);

      if (error) throw error;
      
      setPageStatuses(prev =>
        prev.map(ps => ps.id === id ? { ...ps, icon } : ps)
      );
    } catch (err) {
      console.error('Error updating icon:', err);
      alert('Failed to update icon');
    }
  };

  const createCategory = async () => {
    if (!newCategoryName.trim()) {
      alert('Category name is required');
      return;
    }

    try {
      const maxOrder = Math.max(...categories.map(c => c.order), 0);
      const { error } = await supabase
        .from('menu_categories')
        .insert({
          name: newCategoryName.trim(),
          emoji: newCategoryEmoji || '📁',
          order: maxOrder + 1,
          visible: true
        });

      if (error) throw error;
      
      setNewCategoryName('');
      setNewCategoryEmoji('');
      await loadData();
    } catch (err) {
      console.error('Error creating category:', err);
      alert('Failed to create category');
    }
  };

  const updateCategory = async (category: MenuCategory) => {
    try {
      const { error } = await supabase
        .from('menu_categories')
        .update({
          name: category.name,
          emoji: category.emoji,
          order: category.order,
          visible: category.visible
        })
        .eq('id', category.id);

      if (error) throw error;
      
      setEditingCategory(null);
      await loadData();
    } catch (err) {
      console.error('Error updating category:', err);
      alert('Failed to update category');
    }
  };

  const deleteCategory = async (id: string) => {
    if (!confirm('Are you sure you want to delete this category? Pages in this category will be uncategorized.')) {
      return;
    }

    try {
      // First, unassign all pages from this category
      const { error: updateError } = await supabase
        .from('page_status')
        .update({ category_id: null })
        .eq('category_id', id);

      if (updateError) throw updateError;

      // Then delete the category
      const { error: deleteError } = await supabase
        .from('menu_categories')
        .delete()
        .eq('id', id);

      if (deleteError) throw deleteError;
      
      await loadData();
    } catch (err) {
      console.error('Error deleting category:', err);
      alert('Failed to delete category');
    }
  };

  const toggleCategoryVisibility = async (id: string, visible: boolean) => {
    try {
      const { error } = await supabase
        .from('menu_categories')
        .update({ visible })
        .eq('id', id);

      if (error) throw error;
      await loadData();
    } catch (err) {
      console.error('Error toggling category visibility:', err);
      alert('Failed to toggle category visibility');
    }
  };

  const discoverNewPages = async () => {
    try {
      setLoading(true);

      // Define all routes from the application
      const allRoutes = [
        { path: '/', label: 'Dashboard', icon: 'LayoutDashboard', category: 'Dashboard & System' },
        { path: '/system', label: 'System Overview', icon: 'Activity', category: 'Dashboard & System' },
        { path: '/monitoring', label: 'System Monitoring', icon: 'Bell', category: 'Dashboard & System' },
        { path: '/errors', label: 'Error Tracking', icon: 'AlertTriangle', category: 'Dashboard & System' },
        { path: '/api', label: 'API Diagnostics', icon: 'Activity', category: 'Dashboard & System' },
        { path: '/analytics', label: 'Analytics', icon: 'TrendingUp', category: 'Dashboard & System' },
        { path: '/performance', label: 'Performance', icon: 'Zap', category: 'Dashboard & System' },
        { path: '/exports', label: 'Data Export System', icon: 'FileText', category: 'Dashboard & System' },

        { path: '/users', label: 'Users & Roles', icon: 'ShieldCheck', category: 'Authentication & Users' },
        { path: '/audit', label: 'Audit Log', icon: 'ScrollText', category: 'Authentication & Users' },

        { path: '/customers', label: 'Customer List', icon: 'Users', category: 'Customers' },
        { path: '/customers/import', label: 'Test Import', icon: 'Upload', category: 'Customers' },
        { path: '/customers/export', label: 'Customer Export', icon: 'Download', category: 'Customers' },
        { path: '/customers/merge', label: 'Customer Merge', icon: 'GitBranch', category: 'Customers' },
        { path: '/customers/full-import', label: 'Full Historical Import', icon: 'Database', category: 'Customers' },
        { path: '/associations', label: 'HOA/Associations', icon: 'Building', category: 'Customers' },
        { path: '/associations/properties', label: 'Property Management', icon: 'Building', category: 'Customers' },
        { path: '/associations/bulk-operations', label: 'Bulk Operations', icon: 'Database', category: 'Customers' },

        { path: '/dispatch', label: 'Dispatch Dashboard', icon: 'Truck', category: 'Dispatch & Operations' },
        { path: '/dispatch/schedule', label: 'Schedule', icon: 'Calendar', category: 'Dispatch & Operations' },
        { path: '/dispatch/assignments', label: 'Job Assignments', icon: 'Route', category: 'Dispatch & Operations' },
        { path: '/dispatch/technicians', label: 'Technicians', icon: 'Users', category: 'Dispatch & Operations' },
        { path: '/parts', label: 'Parts Requests', icon: 'Package', category: 'Dispatch & Operations' },
        { path: '/parts/inventory', label: 'Parts Inventory', icon: 'ShoppingCart', category: 'Dispatch & Operations' },
        { path: '/parts/requests', label: 'Request Management', icon: 'ClipboardList', category: 'Dispatch & Operations' },

        { path: '/contact-center/agent-console', label: 'Agent Console', icon: 'Headphones', category: 'Contact Center' },
        { path: '/contact-center/supervisor', label: 'Supervisor Dashboard', icon: 'BarChart3', category: 'Contact Center' },
        { path: '/contact-center/queue-management', label: 'Queue Management', icon: 'Users', category: 'Contact Center' },
        { path: '/contact-center/call-history', label: 'Call History', icon: 'ScrollText', category: 'Contact Center' },
        { path: '/contact-center/analytics', label: 'Call Analytics', icon: 'BarChart3', category: 'Contact Center' },
        { path: '/contact-center/conversions', label: 'Call Conversions', icon: 'TrendingUp', category: 'Contact Center' },
        { path: '/contact-center/dni-attribution', label: 'DNI Attribution', icon: 'Phone', category: 'Contact Center' },
        { path: '/contact-center/settings', label: 'Settings', icon: 'Settings', category: 'Contact Center' },

        { path: '/ai-voice-agents', label: 'AI Voice Overview', icon: 'Phone', category: 'AI Voice Agents' },
        { path: '/ai-voice-agents/companies', label: 'Companies', icon: 'Building', category: 'AI Voice Agents' },
        { path: '/ai-voice-agents/knowledge-base', label: 'Knowledge Base', icon: 'BookOpen', category: 'AI Voice Agents' },
        { path: '/ai-voice-agents/rules', label: 'Agent Rules', icon: 'FileText', category: 'AI Voice Agents' },
        { path: '/ai-voice-agents/phone-numbers', label: 'Phone Numbers', icon: 'Phone', category: 'AI Voice Agents' },
        { path: '/ai-voice-agents/performance', label: 'Performance Dashboard', icon: 'BarChart3', category: 'AI Voice Agents' },
        { path: '/ai-voice-agents/mcp', label: 'MCP Settings', icon: 'Plug', category: 'AI Voice Agents' },

        { path: '/memberships', label: 'Memberships', icon: 'Users', category: 'Memberships & Plans' },
        { path: '/plans', label: 'Service Plans', icon: 'Layers', category: 'Memberships & Plans' },

        { path: '/accounting', label: 'Accounting Overview', icon: 'DollarSign', category: 'Financials & Payments' },
        { path: '/accounting/chart-of-accounts', label: 'Chart of Accounts', icon: 'BookOpen', category: 'Financials & Payments' },
        { path: '/accounting/journals', label: 'Journal Entries', icon: 'Calculator', category: 'Financials & Payments' },
        { path: '/accounting/ar/invoices', label: 'AR Invoices', icon: 'Receipt', category: 'Financials & Payments' },
        { path: '/accounting/ap/bills', label: 'AP Bills', icon: 'CreditCard', category: 'Financials & Payments' },
        { path: '/accounting/inventory', label: 'Inventory', icon: 'Package', category: 'Financials & Payments' },
        { path: '/accounting/reports', label: 'Financial Reports', icon: 'FileText', category: 'Financials & Payments' },
        { path: '/accounting/period-close', label: 'Period Close', icon: 'Settings', category: 'Financials & Payments' },
        { path: '/payments', label: 'Payment Processing', icon: 'CreditCard', category: 'Financials & Payments' },
        { path: '/payments/history', label: 'Transaction History', icon: 'CreditCard', category: 'Financials & Payments' },
        { path: '/payments/reports', label: 'Payment Reports', icon: 'FileText', category: 'Financials & Payments' },
        { path: '/payments/settings', label: 'Payment Settings', icon: 'Settings', category: 'Financials & Payments' },

        { path: '/warranty', label: 'Warranty System', icon: 'Shield', category: 'Warranty & Claims' },
        { path: '/warranty/dashboard', label: 'Warranty Dashboard', icon: 'BarChart3', category: 'Warranty & Claims' },
        { path: '/warranty/equipment', label: 'Equipment Registry', icon: 'Package', category: 'Warranty & Claims' },
        { path: '/warranty/agreements', label: 'Service Agreements', icon: 'FileText', category: 'Warranty & Claims' },
        { path: '/warranty/claims', label: 'Claims Processing', icon: 'FileX', category: 'Warranty & Claims' },
        { path: '/warranty/contractors', label: 'Contractors', icon: 'Users', category: 'Warranty & Claims' },
        { path: '/warranty/coverage', label: 'Coverage Plans', icon: 'Shield', category: 'Warranty & Claims' },
        { path: '/warranty/reports', label: 'Reports', icon: 'FileText', category: 'Warranty & Claims' },
        { path: '/warranty/compliance', label: 'Compliance', icon: 'ShieldCheck', category: 'Warranty & Claims' },
        { path: '/warranty/policies', label: 'Policy Management', icon: 'FileText', category: 'Warranty & Claims' },
        { path: '/warranty/customers', label: 'Warranty Customers', icon: 'Users', category: 'Warranty & Claims' },

        { path: '/vendors', label: 'Vendor Management', icon: 'Building', category: 'Vendors' },

        { path: '/messaging/sms-analytics', label: 'SMS Analytics', icon: 'MessageSquare', category: 'Messaging & Reviews' },
        { path: '/messaging/sms-settings', label: 'SMS Settings', icon: 'Settings', category: 'Messaging & Reviews' },
        { path: '/reviews', label: 'Reviews Management', icon: 'Star', category: 'Messaging & Reviews' },
        { path: '/reviews/dashboard', label: 'Review Analytics', icon: 'BarChart3', category: 'Messaging & Reviews' },
        { path: '/reviews/responses', label: 'Response Management', icon: 'MessageSquare', category: 'Messaging & Reviews' },

        { path: '/integrations', label: 'Integrations Hub', icon: 'Plug', category: 'Integrations & API' },
        { path: '/integrations/successware', label: 'SuccessWare Hub', icon: 'Plug', category: 'Integrations & API' },
        { path: '/integrations/successware/credentials', label: 'SW API Credentials', icon: 'ShieldCheck', category: 'Integrations & API' },
        { path: '/integrations/successware/catalog', label: 'SW Operations Catalog', icon: 'BookOpen', category: 'Integrations & API' },
        { path: '/integrations/successware/fields', label: 'SW Field Catalog', icon: 'Database', category: 'Integrations & API' },
        { path: '/integrations/successware/runner', label: 'SW API Runner', icon: 'Activity', category: 'Integrations & API' },
        { path: '/integrations/successware/logs', label: 'SW Logs & Health', icon: 'ScrollText', category: 'Integrations & API' },

        { path: '/reports/revenue', label: 'Revenue Reports', icon: 'DollarSign', category: 'Reports & Analytics' },
        { path: '/reports/claims-ratio', label: 'Claims Ratio Analysis', icon: 'BarChart3', category: 'Reports & Analytics' },
        { path: '/sales', label: 'Sales & Revenue', icon: 'CreditCard', category: 'Reports & Analytics' },
        { path: '/traffic', label: 'Traffic & Funnels', icon: 'LineChart', category: 'Reports & Analytics' },
        { path: '/conversions', label: 'Conversion Tracking', icon: 'BarChart3', category: 'Reports & Analytics' },

        { path: '/forms', label: 'Forms Library', icon: 'FileCheck', category: 'Forms & Imports' },
        { path: '/forms/management', label: 'Form Management', icon: 'Settings', category: 'Forms & Imports' },
        { path: '/imports', label: 'Data Import System', icon: 'Database', category: 'Forms & Imports' },

        { path: '/settings', label: 'System Settings', icon: 'Settings', category: 'Admin & Dev' },
        { path: '/settings/page-status', label: 'Page Status', icon: 'Activity', category: 'Admin & Dev' },
        { path: '/site-settings', label: 'Site Settings', icon: 'Settings', category: 'Admin & Dev' },
        { path: '/renewals', label: 'Renewals', icon: 'RefreshCcw', category: 'Admin & Dev' },
      ];

      // Get existing routes from database
      const { data: existingPages } = await supabase
        .from('page_status')
        .select('route');

      const existingRoutes = new Set(existingPages?.map(p => p.route) || []);

      // Find missing routes
      const missingRoutes = allRoutes.filter(route => !existingRoutes.has(route.path));

      if (missingRoutes.length === 0) {
        alert('✅ All routes are already in the database!');
        return;
      }

      // Create category mapping
      const categoryMap = new Map();
      const categoryOrder = new Map();

      // First, ensure all categories exist
      for (const route of allRoutes) {
        if (!categoryMap.has(route.category)) {
          const maxOrder = Math.max(...Array.from(categoryOrder.values()), 0);
          categoryOrder.set(route.category, maxOrder + 1);

          const { data: category } = await supabase
            .from('menu_categories')
            .insert({
              name: route.category,
              emoji: getCategoryEmoji(route.category),
              order: categoryOrder.get(route.category),
              visible: true
            })
            .select()
            .single();

          if (category) {
            categoryMap.set(route.category, category.id);
          }
        }
      }

      // Add missing pages
      const pagesToInsert = missingRoutes.map(route => ({
        route: route.path,
        label: route.label,
        status: 'yellow' as const, // Default to "In Progress"
        visible: true,
        category_id: categoryMap.get(route.category),
        order: 100, // Default order
        icon: route.icon
      }));

      const { error } = await supabase
        .from('page_status')
        .insert(pagesToInsert);

      if (error) throw error;

      alert(`✅ Successfully added ${missingRoutes.length} new pages to the database!`);
      await loadData();

    } catch (err) {
      console.error('Error discovering pages:', err);
      alert('❌ Failed to discover new pages. Check console for details.');
    } finally {
      setLoading(false);
    }
  };

  const getCategoryEmoji = (categoryName: string): string => {
    const emojiMap: Record<string, string> = {
      'Dashboard & System': '📊',
      'Authentication & Users': '🔐',
      'Customers': '👥',
      'Dispatch & Operations': '🚛',
      'Contact Center': '☎️',
      'AI Voice Agents': '🤖',
      'Memberships & Plans': '🤝',
      'Financials & Payments': '💰',
      'Warranty & Claims': '🏠',
      'Vendors': '👔',
      'Messaging & Reviews': '📱',
      'Integrations & API': '🔌',
      'Reports & Analytics': '📊',
      'Forms & Imports': '📋',
      'Admin & Dev': '⚙️'
    };
    return emojiMap[categoryName] || '📁';
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'green': return 'bg-green-500';
      case 'yellow': return 'bg-yellow-500';
      case 'red': return 'bg-red-500';
      default: return 'bg-gray-500';
    }
  };

  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'green': return 'Functional';
      case 'yellow': return 'In Progress';
      case 'red': return 'Not Functional';
      default: return status;
    }
  };

  const renderIcon = (iconName: string | null) => {
    if (!iconName) return null;
    const IconComponent = (LucideIcons as any)[iconName];
    if (!IconComponent) return null;
    return <IconComponent className="w-4 h-4" />;
  };

  const filteredPages = pageStatuses.filter(ps => {
    const matchesFilter = filter === 'all' || ps.status === filter;
    const matchesSearch = searchTerm === '' || 
      ps.route.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (ps.label?.toLowerCase().includes(searchTerm.toLowerCase()) ?? false);
    return matchesFilter && matchesSearch;
  });

  const statusCounts = {
    green: pageStatuses.filter(ps => ps.status === 'green').length,
    yellow: pageStatuses.filter(ps => ps.status === 'yellow').length,
    red: pageStatuses.filter(ps => ps.status === 'red').length,
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="text-lg">Loading menu management system...</div>
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="mb-6">
        <h1 className="text-3xl font-bold mb-2">Menu Management System</h1>
        <p className="text-gray-600">
          Manage page status, visibility, categories, and menu organization
        </p>
      </div>

      {/* Status Overview */}
      <div className="grid grid-cols-4 gap-4 mb-6">
        <div className="bg-white p-4 rounded-lg shadow">
          <div className="flex items-center justify-between">
            <div>
              <div className="text-sm text-gray-600">Total Pages</div>
              <div className="text-2xl font-bold text-blue-600">{pageStatuses.length}</div>
            </div>
            <div className="w-12 h-12 bg-blue-500 rounded-full flex items-center justify-center text-white text-xl">
              📄
            </div>
          </div>
        </div>
        <div className="bg-white p-4 rounded-lg shadow">
          <div className="flex items-center justify-between">
            <div>
              <div className="text-sm text-gray-600">Functional</div>
              <div className="text-2xl font-bold text-green-600">{statusCounts.green}</div>
            </div>
            <div className="w-12 h-12 bg-green-500 rounded-full"></div>
          </div>
        </div>
        <div className="bg-white p-4 rounded-lg shadow">
          <div className="flex items-center justify-between">
            <div>
              <div className="text-sm text-gray-600">In Progress</div>
              <div className="text-2xl font-bold text-yellow-600">{statusCounts.yellow}</div>
            </div>
            <div className="w-12 h-12 bg-yellow-500 rounded-full"></div>
          </div>
        </div>
        <div className="bg-white p-4 rounded-lg shadow">
          <div className="flex items-center justify-between">
            <div>
              <div className="text-sm text-gray-600">Not Functional</div>
              <div className="text-2xl font-bold text-red-600">{statusCounts.red}</div>
            </div>
            <div className="w-12 h-12 bg-red-500 rounded-full"></div>
          </div>
        </div>
      </div>

      {/* Action Buttons */}
      <div className="flex flex-wrap gap-4 mb-6">
        <button
          onClick={() => setShowCategoryManager(!showCategoryManager)}
          className="px-4 py-2 bg-purple-600 text-white rounded-lg font-medium hover:bg-purple-700"
        >
          {showCategoryManager ? 'Hide' : 'Show'} Category Manager
        </button>
        <button
          onClick={discoverNewPages}
          className="px-4 py-2 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700"
        >
          🔍 Discover New Pages
        </button>
        <button
          onClick={mergeDuplicateCategories}
          className="px-4 py-2 bg-orange-600 text-white rounded-lg font-medium hover:bg-orange-700"
        >
          🔄 Merge Duplicates
        </button>
        <button
          onClick={() => setShowMergeTool(!showMergeTool)}
          className="px-4 py-2 bg-red-600 text-white rounded-lg font-medium hover:bg-red-700"
        >
          {showMergeTool ? 'Hide' : 'Show'} Merge Tool
        </button>
      </div>

      {/* Merge Tool */}
      {showMergeTool && (
        <div className="bg-white rounded-lg shadow p-6 mb-6">
          <h2 className="text-xl font-bold mb-4">Duplicate Category Merger</h2>
          <p className="text-gray-600 mb-4">
            This tool will find and merge duplicate categories, moving all pages from duplicate categories to the first occurrence.
          </p>
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-4">
            <h3 className="font-semibold text-yellow-800 mb-2">⚠️ Warning</h3>
            <p className="text-yellow-700 text-sm">
              This action cannot be undone. Make sure to backup your data first.
              All pages from duplicate categories will be moved to the primary category.
            </p>
          </div>
          <button
            onClick={mergeDuplicateCategories}
            className="px-6 py-3 bg-red-600 text-white rounded-lg font-medium hover:bg-red-700"
          >
            🚨 Merge All Duplicate Categories
          </button>
        </div>
      )}

      {/* Category Manager */}
      {showCategoryManager && (
        <div className="bg-white rounded-lg shadow p-6 mb-6">
          <h2 className="text-xl font-bold mb-4">Menu Category Manager</h2>
          
          {/* Add New Category */}
          <div className="bg-blue-50 p-4 rounded-lg mb-4">
            <h3 className="font-semibold mb-3">Add New Category</h3>
            <div className="flex gap-3">
              <input
                type="text"
                placeholder="Category Name"
                value={newCategoryName}
                onChange={(e) => setNewCategoryName(e.target.value)}
                className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
              <input
                type="text"
                placeholder="Emoji"
                value={newCategoryEmoji}
                onChange={(e) => setNewCategoryEmoji(e.target.value)}
                className="w-20 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
              <button
                onClick={createCategory}
                className="px-4 py-2 bg-green-600 text-white rounded-lg font-medium hover:bg-green-700"
              >
                Add Category
              </button>
            </div>
          </div>

          {/* Category List */}
          <div className="space-y-2">
            {categories.map((cat) => (
              <div key={cat.id} className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                {editingCategory?.id === cat.id ? (
                  <>
                    <input
                      type="text"
                      value={editingCategory.emoji}
                      onChange={(e) => setEditingCategory({ ...editingCategory, emoji: e.target.value })}
                      className="w-16 px-2 py-1 border border-gray-300 rounded"
                    />
                    <input
                      type="text"
                      value={editingCategory.name}
                      onChange={(e) => setEditingCategory({ ...editingCategory, name: e.target.value })}
                      className="flex-1 px-2 py-1 border border-gray-300 rounded"
                    />
                    <input
                      type="number"
                      value={editingCategory.order}
                      onChange={(e) => setEditingCategory({ ...editingCategory, order: parseInt(e.target.value) })}
                      className="w-20 px-2 py-1 border border-gray-300 rounded"
                    />
                    <button
                      onClick={() => updateCategory(editingCategory)}
                      className="px-3 py-1 bg-green-600 text-white rounded hover:bg-green-700"
                    >
                      Save
                    </button>
                    <button
                      onClick={() => setEditingCategory(null)}
                      className="px-3 py-1 bg-gray-600 text-white rounded hover:bg-gray-700"
                    >
                      Cancel
                    </button>
                  </>
                ) : (
                  <>
                    <div className="text-2xl w-12 text-center">{cat.emoji}</div>
                    <div className="flex-1 font-medium">{cat.name}</div>
                    <div className="text-sm text-gray-500">Order: {cat.order}</div>
                    <button
                      onClick={() => toggleCategoryVisibility(cat.id, !cat.visible)}
                      className={`px-3 py-1 rounded text-xs font-medium ${
                        cat.visible
                          ? 'bg-green-100 text-green-700'
                          : 'bg-gray-100 text-gray-700'
                      }`}
                    >
                      {cat.visible ? 'Visible' : 'Hidden'}
                    </button>
                    <button
                      onClick={() => setEditingCategory(cat)}
                      className="px-3 py-1 bg-blue-600 text-white rounded hover:bg-blue-700"
                    >
                      Edit
                    </button>
                    <button
                      onClick={() => deleteCategory(cat.id)}
                      className="px-3 py-1 bg-red-600 text-white rounded hover:bg-red-700"
                    >
                      Delete
                    </button>
                  </>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="bg-white rounded-lg shadow p-4 mb-6">
        <div className="flex flex-col md:flex-row gap-4">
          <div className="flex-1">
            <input
              type="text"
              placeholder="Search pages..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>
          <div className="flex gap-2">
            <button
              onClick={() => setFilter('all')}
              className={`px-4 py-2 rounded-lg font-medium ${
                filter === 'all'
                  ? 'bg-blue-600 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              All ({pageStatuses.length})
            </button>
            <button
              onClick={() => setFilter('green')}
              className={`px-4 py-2 rounded-lg font-medium ${
                filter === 'green'
                  ? 'bg-green-600 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              Green ({statusCounts.green})
            </button>
            <button
              onClick={() => setFilter('yellow')}
              className={`px-4 py-2 rounded-lg font-medium ${
                filter === 'yellow'
                  ? 'bg-yellow-600 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              Yellow ({statusCounts.yellow})
            </button>
            <button
              onClick={() => setFilter('red')}
              className={`px-4 py-2 rounded-lg font-medium ${
                filter === 'red'
                  ? 'bg-red-600 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              Red ({statusCounts.red})
            </button>
          </div>
        </div>
      </div>

      {/* Page List */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="mb-4 p-4 bg-blue-50 border-b">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-semibold text-blue-900">Page Management</h3>
            <div className="text-sm text-blue-700">
              💡 <strong>Tip:</strong> Drag and drop rows to reorder pages, or use the order input field
            </div>
          </div>
        </div>
        <DragDropContext onDragEnd={handleDragEnd}>
          <Droppable droppableId="pages-table">
            {(provided) => (
              <div {...provided.droppableProps} ref={provided.innerRef}>
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-16">
                          Visible
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Route
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Label
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Icon
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Category
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-20">
                          Order
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Status
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Change Status
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {filteredPages.map((ps, index) => (
                        <Draggable key={ps.id} draggableId={ps.id} index={index}>
                          {(provided, snapshot) => (
                            <tr
                              ref={provided.innerRef}
                              {...provided.draggableProps}
                              {...provided.dragHandleProps}
                              className={`hover:bg-gray-50 ${snapshot.isDragging ? 'bg-blue-50' : ''}`}
                            >
                              <td className="px-4 py-3 whitespace-nowrap">
                                <button
                                  onClick={() => updatePageVisibility(ps.id, !ps.visible)}
                                  className={`w-8 h-8 rounded flex items-center justify-center ${
                                    ps.visible
                                      ? 'bg-green-100 text-green-600'
                                      : 'bg-gray-100 text-gray-400'
                                  }`}
                                  title={ps.visible ? 'Hide from menu' : 'Show in menu'}
                                >
                                  {ps.visible ? '👁️' : '👁️‍🗨️'}
                                </button>
                              </td>
                              <td className="px-4 py-3 whitespace-nowrap text-sm font-mono text-gray-900">
                                {ps.route}
                              </td>
                              <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-700">
                                {ps.label || '-'}
                              </td>
                              <td className="px-4 py-3 whitespace-nowrap">
                                {editingPageId === ps.id ? (
                                  <div className="flex gap-1">
                                    <select
                                      value={ps.icon || ''}
                                      onChange={(e) => {
                                        updatePageIcon(ps.id, e.target.value || null);
                                        setEditingPageId(null);
                                      }}
                                      className="text-xs border border-gray-300 rounded px-1 py-1"
                                    >
                                      <option value="">None</option>
                                      {commonIcons.map(icon => (
                                        <option key={icon} value={icon}>{icon}</option>
                                      ))}
                                    </select>
                                  </div>
                                ) : (
                                  <button
                                    onClick={() => setEditingPageId(ps.id)}
                                    className="flex items-center gap-1 text-sm text-blue-600 hover:text-blue-700"
                                  >
                                    {renderIcon(ps.icon)}
                                    <span className="text-xs">{ps.icon || 'Set'}</span>
                                  </button>
                                )}
                              </td>
                              <td className="px-4 py-3 whitespace-nowrap">
                                <select
                                  value={ps.category_id || ''}
                                  onChange={(e) => updatePageCategory(ps.id, e.target.value || null)}
                                  className="text-sm border border-gray-300 rounded px-2 py-1"
                                >
                                  <option value="">Uncategorized</option>
                                  {categories.map(cat => (
                                    <option key={cat.id} value={cat.id}>
                                      {cat.emoji} {cat.name}
                                    </option>
                                  ))}
                                </select>
                              </td>
                              <td className="px-4 py-3 whitespace-nowrap">
                                <input
                                  type="number"
                                  value={ps.order}
                                  onChange={(e) => updatePageOrder(ps.id, parseInt(e.target.value) || 0)}
                                  className="w-16 text-sm border border-gray-300 rounded px-2 py-1"
                                />
                              </td>
                              <td className="px-4 py-3 whitespace-nowrap">
                                <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium text-white ${getStatusColor(ps.status)}`}>
                                  {getStatusLabel(ps.status)}
                                </span>
                              </td>
                              <td className="px-4 py-3 whitespace-nowrap text-sm">
                                <div className="flex gap-2">
                                  <button
                                    onClick={() => updateStatus(ps.id, 'green')}
                                    disabled={ps.status === 'green'}
                                    className={`px-3 py-1 rounded font-medium text-xs ${
                                      ps.status === 'green'
                                        ? 'bg-gray-200 text-gray-400 cursor-not-allowed'
                                        : 'bg-green-100 text-green-700 hover:bg-green-200'
                                    }`}
                                  >
                                    Green
                                  </button>
                                  <button
                                    onClick={() => updateStatus(ps.id, 'yellow')}
                                    disabled={ps.status === 'yellow'}
                                    className={`px-3 py-1 rounded font-medium text-xs ${
                                      ps.status === 'yellow'
                                        ? 'bg-gray-200 text-gray-400 cursor-not-allowed'
                                        : 'bg-yellow-100 text-yellow-700 hover:bg-yellow-200'
                                    }`}
                                  >
                                    Yellow
                                  </button>
                                  <button
                                    onClick={() => updateStatus(ps.id, 'red')}
                                    disabled={ps.status === 'red'}
                                    className={`px-3 py-1 rounded font-medium text-xs ${
                                      ps.status === 'red'
                                        ? 'bg-gray-200 text-gray-400 cursor-not-allowed'
                                        : 'bg-red-100 text-red-700 hover:bg-red-200'
                                    }`}
                                  >
                                    Red
                                  </button>
                                </div>
                              </td>
                            </tr>
                          )}
                        </Draggable>
                      ))}
                      {provided.placeholder}
                    </tbody>
                  </table>
                </div>

                {filteredPages.length === 0 && (
                  <div className="p-8 text-center text-gray-400">
                    No pages match your filter criteria
                  </div>
                )}
              </div>
            )}
          </Droppable>
        </DragDropContext>
      </div>

      {/* Legend */}
      <div className="mt-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
        <h3 className="font-semibold text-blue-900 mb-2">Feature Guide</h3>
        <div className="grid grid-cols-2 gap-4 text-sm text-blue-800">
          <div>
            <strong>Visibility Toggle:</strong> Show/hide page in menu
          </div>
          <div>
            <strong>Category Assignment:</strong> Organize pages into menu categories
          </div>
          <div>
            <strong>Order:</strong> Control display order within category (lower = higher)
          </div>
          <div>
            <strong>Icon:</strong> Choose an icon from Lucide React library
          </div>
          <div>
            <strong>Status Colors:</strong> 🟢 Functional | 🟡 In Progress | 🔴 Not Functional
          </div>
          <div>
            <strong>Category Manager:</strong> Add, edit, delete, and reorder menu categories
          </div>
        </div>
      </div>
    </div>
  );
};

export const meta = {
  label: 'Menu Management',
  icon: 'Settings',
  order: 1,
};

export default PageStatusPage;
